import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { useURLParams } from '../../providers/URLParamProvider';
import { CPAActionTypes } from '../../utilities/audits';
import { useQueryParams } from '../../hooks/misc';
import { keys } from '../../utilities/translator/translation_keys';
import SimpleWorkspaceCard from '../SimpleWorkspaceCard';
import SimpleUserCard from '../SimpleUserCard';
import Label from '../action_items/Label';
import { Text } from '../typography';
import { vehicleString } from '../../utilities';
import {
  assessmentStatus,
  defaultAssessmentTypes,
} from '../../constants/strings';
import { timecardStatusTypes as timecardStatus } from '../../pages/hr/Timecards';
import { statusStringLookup as actionItemStatus } from '../action_items/action_item_utilities';
import { observationStatus } from '../../utilities/observations';
import { incidentStatusTypes as incidentStatus } from '../../utilities/incidents';
import { expenseStatusTypes as expenseStatus } from '../../pages/hr/Expenses';

export const useTableURLParams = () => {
  const navigate = useNavigate();
  const location = useLocation();
  let { segment } = useURLParams();
  const { getParam } = useQueryParams();
  const modalOpen = getParam('modalName');

  const tableFiltersToURL = (filters, pagination, sorting, allWorkspaces) => {
    const searchParams = new URLSearchParams(location.search);
    const templates = Array.from(searchParams.entries()).find(
      ([key, value]) => key === 'tab' && value === 'templates',
    );

    if (!!templates) {
      segment = 'templates';
    }

    for (const key of Array.from(searchParams.keys())) {
      if (
        tableFilterParams[segment]?.includes(key) &&
        !defaultSearchParams[segment]?.includes(key) &&
        key !== 'parentId'
      ) {
        searchParams.delete(key);
      }
    }
    const multiFilters = [
      'assignee',
      'reviewer',
      'creator',
      'workspaceId',
      'closer',
      'vehicle',
      'supervisor',
      'participants',
      'workspace',
      'status',
      'extension',
      'user',
      'users',
      'workspaces',
      'reportTo',
    ];

    filters?.forEach(({ id, value }) => {
      if (!!value && !!tableFilterParams[segment]?.length) {
        if (Array.isArray(value)) {
          if (id === 'labels') {
            searchParams.set(id, value.join(','));
          } else if (multiFilters?.includes(id)) {
            searchParams.set(id, value);
          } else {
            if (value?.length === 2) {
              searchParams.set(id, `${value[0]}__${value[1] ?? ''}`);
            } else {
              searchParams.set(id, [`${value[0]}`]);
            }
          }
        } else {
          searchParams.set(id, value);
        }
      }
    });
    subFilterParams[segment]?.forEach((key) => {
      if (!filters.find((filter) => filter.id === key)) {
        searchParams.delete(key);
      }
    });
    if (pagination) {
      searchParams.set('pageIndex', pagination.pageIndex);
      searchParams.set('pageSize', pagination.pageSize);
    }
    if (sorting && sorting.length > 0) {
      searchParams.set('sort', sorting[0].id);
      searchParams.set('desc', sorting[0].desc);
    }
    if (!companyWideTables.includes(segment)) {
      searchParams.set('allWorkspaces', allWorkspaces ? 'TRUE' : 'FALSE');
    }
    if (companyWideTables.includes(segment)) {
      const tab = getParam('tab');
      if (tab !== 'workspace') {
        searchParams.set('allWorkspaces', 'TRUE');
      }
    }
    const nextSearch = `?${searchParams.toString()}`;
    if (!modalOpen && nextSearch !== location.search) {
      navigate({ pathname: location.pathname, search: nextSearch });
    }
  };

  return { tableFiltersToURL };
};
export const formatFilters = (originalField, originalValue, userId) => {
  let operator = 'eq';
  let value = originalValue;
  let field = originalField;

  switch (originalField) {
    case 'isObservation':
      switch (value) {
        case 'FINDING':
          field = 'isObservation';
          value = 'true';
          break;
        case 'OFI':
          field = 'isObservation';
          value = 'false';
          break;
        default:
          field = 'isRepeat';
          value = 'true';
          break;
      }
      break;
    case 'type':
      if (originalValue === 'CPA') {
        operator = 'or';
        value = [
          CPAActionTypes.interim,
          CPAActionTypes.final,
          CPAActionTypes.review,
          CPAActionTypes.accept,
        ];
      } else {
        operator = 'iLike';
        value = `%${value}%`;
        break;
      }
      break;
    case 'crossWorkspace':
      field = 'workspaceId';
      operator = 'eq';
      value = `${null}`;
      break;
    case 'allWorkspaces':
      field = 'assignee';
      operator = 'eq';
      value = userId;
      break;
    case 'auditType':
    case 'description':
    case 'title':
    case 'documentNumber':
    case 'documentString':
    case 'firstName':
    case 'lastName':
    case 'email':
    case 'externalActivityId':
    case 'externalId':
    case 'narrative':
    case 'subject':
    case 'make':
    case 'model':
    case 'subtype':
    case 'year':
    case 'unitNumber':
    case 'location':
    case 'name':
    case 'category':
    case 'incidentEmergencyMessage':
    case 'locations':
    case 'poNumber':
      operator = 'iLike';
      value = `%${value}%`;
      break;
    case 'assignee':
    case 'reviewer':
    case 'creator':
    case 'workspace':
    case 'closer':
    case 'vehicle':
    case 'supervisor':
    case 'user':
    case 'userResponsible':
      field = `${field}Id`;
      operator = 'or';
      value = originalValue?.split(',');
      break;
    case 'reportTo':
      field = 'reportToId';
      operator = 'or';
      value = originalValue?.split(',');
      break;
    case 'status':
    case 'extension':
      field = `${field}`;
      operator = 'in';
      value = originalValue?.split(',');
      break;
    case 'workspaceId':
      operator = 'or';
      value = originalValue?.split(',');
      break;
    case 'Passed':
      field = 'passed';
      break;
    case 'riskScore':
    case 'residualRiskScore':
      operator = 'between';
      value = originalValue?.split(',');
      break;
    case 'closedAt':
    case 'dateCompleted':
    case 'dateTime':
    case 'dateCreated':
    case 'dateModified':
    case 'dueDate':
    case 'dateOfIncident':
    case 'lastInspectionDate':
    case 'endOfLife':
    case 'scheduledStartDate':
    case 'scheduledEndDate':
    case 'dateOfExpense':
    case 'startTime':
    case 'endTime':
    case 'lastTime':
    case 'dateAccepted':
    case 'nextReviewDate':
    case 'lastReviewDate':
      const [min, max] = originalValue;
      const minDateFormat =
        originalField === 'dateOfIncident'
          ? 'YYYY-MM-DD'
          : 'YYYY-MM-DD 00:00:00';

      const maxDateFormat =
        originalField === 'dateOfIncident'
          ? 'YYYY-MM-DD'
          : 'YYYY-MM-DD 23:59:59';
      const dateMin =
        min && moment(min).isValid() ? moment(min).format(minDateFormat) : null;
      const dateMax =
        max && moment(max).isValid() ? moment(max).format(maxDateFormat) : null;
      if (dateMin && !dateMax) {
        operator = 'gte';
        value = [dateMin];
      } else if (!dateMin && dateMax) {
        operator = 'lte';
        value = [dateMax];
      } else if (dateMin && dateMax) {
        operator = 'between';
        value = [dateMin, dateMax];
      } else {
        return null;
      }
      break;
    default:
      break;
  }

  return { field, operator, value };
};
export const formatSubFilter = (originalField, originalValue) => {
  switch (originalField) {
    case 'templateTitle':
      return {
        association: 'template',
        field: 'title',
        operator: 'iLike',
        value: [`%${originalValue}%`],
      };
    case 'participants':
    case 'assignees':
    case 'users':
      return {
        association: originalField,
        field: 'userId',
        operator: 'in',
        value: originalValue?.split(','),
      };
    case 'labels':
      return {
        association: originalField,
        field: 'labelId',
        operator: 'or',
        value: [...originalValue?.toString()?.split(',')],
      };
    case 'workspaces':
      return {
        association: originalField,
        field: 'id',
        operator: 'or',
        value: [...originalValue?.toString()?.split(',')],
      };
    case 'workRoles':
      if (originalValue !== 'None') {
        return {
          association: originalField,
          field: 'title',
          operator: 'iLike',
          value: [`%${originalValue}%`],
        };
      }
      break;
    case 'inspection.template.title':
      return {
        association: 'inspection.template',
        field: 'title',
        operator: 'iLike',
        value: [`%${originalValue}%`],
      };
    case 'activity':
      return {
        association: 'activity',
        field: 'description',
        operator: 'iLike',
        value: [`%${originalValue}%`],
      };
    case 'requirement':
      return {
        association: 'requirement',
        field: 'title',
        operator: 'iLike',
        value: [`%${originalValue}%`],
      };

    case 'assigneeInfo':
      return {
        association: 'assigneeInfo',
        field: 'userId',
        operator: 'in',
        value: originalValue?.split(','),
      };
    case 'vehicles':
      return {
        association: 'vehicles',
        field: 'vehicleId',
        operator: 'in',
        value: originalValue?.split(','),
      };
    case 'files':
      return {
        association: 'files',
        field: 'id',
        operator: 'ne',
        value: ['null'],
      };
    case 'incidents':
      return {
        association: 'incidents',
        field: 'externalId',
        operator: 'iLike',
        value: [`%${originalValue}%`],
      };
    case 'hasRisks':
      return {
        association: 'hasRisks',
        field: 'placeholder',
        operator: 'eq',
        value: [originalValue],
      };
    default:
      return {};
  }
};

export const commonFilters = ['dateCreated', 'dateModified', 'creator', 'id'];

export const tableFilterParams = {
  actionitems: [
    ...commonFilters,
    'title',
    'assignee',
    'priority',
    'status',
    'dueDate',
    'dateCompleted',
    'workspaceId',
    'type',
    'description',
    'private',
    'isCorrective',
  ],
  assessments: [
    ...commonFilters,
    'inspectionType',
    // 'isDraft',
    'passed',
    'dateCompleted',
    'vehicle',
    'workspace',
    'documentNumber',
    'private',
    'subject',
    'crossWorkspace',
    'status',
  ],
  incidents: [
    ...commonFilters,
    'title',
    'type',
    'subtype',
    'status',
    'workspace',
    'dateOfIncident',
    'closedAt',
    'private',
    'externalId',
  ],
  observations: [
    ...commonFilters,
    'description',
    'dateTime',
    'status',
    'workspace',
    'reviewer',
    'closer',
    'negative',
    'title',
    'private',
    'externalId',
    'recognition',
    'user',
    'type',
  ],
  audits: [
    ...commonFilters,
    // 'isDraft',
    'dateCompleted',
    'riskLevel',
    'assignee',
    'status',
    'narrative',
    'isObservation',
    'subject',
    'auditType',
    'description',
    'vehicle',
  ],
  cpas: [
    ...commonFilters,
    'narrative',
    'dateCompleted',
    'externalId',
    'dateAccepted',
    'status',
    'type',
    'riskLevel',
    'isObservation',
    'assignee',
    'extensionStatus',
    'isRepeat',
  ],
  templates: [
    ...commonFilters,
    'title',
    'isDraft',
    'revisionNumber',
    'workspace',
    'documentString',
    'templateType',
    'reviewRequired',
    'analysisRequested',
  ],
  users: [
    ...commonFilters,
    'firstName',
    'lastName',
    'email',
    'payrollId',
    'supervisor',
    'role',
    'compliance',
    'status',
    'active',
    'title',
    'description',
    'incidentEmergencyMessage',
    'status',
    'locations',
    'user',
    'active',
  ],
  settings: [
    ...commonFilters,
    'firstName',
    'lastName',
    'email',
    'externalActivityId',
    'payrollId',
    'title',
    'description',
    'supervisor',
    'role',
    'compliance',
    'incidentEmergencyMessage',
    'status',
    'locations',
    'user',
    'active',
  ],
  tasks: [
    ...commonFilters,
    'interval',
    'target',
    'type',
    'workspace',
    'title',
    'scheduledStartDate',
    'scheduledEndDate',
  ],
  assets: [
    ...commonFilters,
    'type',
    'subtype',
    'make',
    'model',
    'unitNumber',
    'year',
    'location',
    'lastInspectionDate',
    'endOfLife',
    'endOfLifeHours',
    'status',
  ],
  files: [
    ...commonFilters,
    'name',
    'extension',
    'user',
    'status',
    'downloadAllowed',
    'expires',
    'type',
    'rootFolder',
    'id',
    'parentId',
    'description',
  ],
  toolbox: [...commonFilters, 'title', 'description', 'isDraft'],
  registry: [
    ...commonFilters,
    'riskScore',
    'residualRiskScore',
    'description',
    'status',
    'impact',
    'probability',
    'comments',
    'workspace',
    'assignee',
    'category',
    'externalId',
    'nextReviewDate',
    'lastReviewDate',
  ],
  timecards: [
    ...commonFilters,
    'workspace',
    'startTime',
    'endTime',
    'status',
    'billableHours',
    'poNumber',
  ],
  expenses: [
    ...commonFilters,
    'dateOfExpense',
    'type',
    'description',
    'total',
    'workspace',
    'status',
  ],
  checkins: [
    ...commonFilters,
    'lastStatus',
    'lastTime',
    'phone',
    'userResponsible',
  ],
  roles: [...commonFilters, 'description', 'title', 'reportTo'],
  certifications: [...commonFilters, 'name', 'user', 'expires'],
  requirements: [...commonFilters, 'title', 'description'],
  quality: [
    ...commonFilters,
    'inspectionType',
    'isDraft',
    'Passed',
    'dateCompleted',
    'vehicle',
    'workspace',
    'documentNumber',
    'private',
    'subject',
    'crossWorkspace',
    'status',
  ],
  analytics: [...commonFilters, 'title'],
  reports: [...commonFilters, 'title'],
  incident_reports: [...commonFilters, 'title', 'isDraft', 'subject'],
};
export const subFilterParams = {
  assets: ['assignees'],
  assessments: ['templateTitle', 'labels', 'participants'],
  actionitems: ['labels', 'participants'],
  incidents: ['labels', 'participants', 'templateTitle', 'hasRisks'],
  audits: ['templateTitle', 'participants', 'labels'],
  cpas: ['users', 'inspection.template.title'],
  checkins: ['users'],
  settings: ['workspaces', 'workRoles'],
  timecards: ['activity'],
  expenses: ['files'],
  files: ['requirement'],
  incident_reports: ['templateTitle', 'incidents'],
  users: ['workspaces'],
  tasks: ['assigneeInfo', 'vehicles'],
};

export const companyWideTables = [
  'audits',
  'settings',
  'tasks',
  'toolbox',
  'risks',
  'registry',
  'timecards',
  'expenses',
  'checkins',
  'roles',
  'certifications',
  'files',
  'requirements',
  'analytics',
  'users',
  'reports',
];

export const tableSortingParams = [
  'pageSize',
  'pageIndex',
  'sort',
  'desc',
  'tab',
  'lockedType',
];

export const getWorkspace = (workspaceId, availableWorkspaces) => {
  return availableWorkspaces?.find((w) => w.id === workspaceId);
};

const getTableSettingsKey = (segment, tab) => {
  return `tableSettings:${segment}_${tab || segment}`;
};

export function saveTableSettings(segment, settings, tab) {
  localStorage.setItem(
    getTableSettingsKey(segment, tab),
    JSON.stringify(settings),
  );
}

export function loadTableSettings(segment, tab) {
  return JSON.parse(
    localStorage.getItem(getTableSettingsKey(segment, tab)) || '{}',
  );
}

export function getDateRange(cachedArr, paramStr) {
  const arr = cachedArr || [];
  const paramArr = (paramStr || '')?.split('__');
  return [arr[0] || paramArr[0] || '', arr[1] || paramArr[1] || ''];
}

export const dateTypes = [
  ['dateCreated', 'dateCreatedMin', 'dateCreatedMax'],
  ['dateCompleted', 'dateCompletedMin', 'dateCompletedMax'],
  ['dateModified', 'dateModifiedMin', 'dateModifiedMax'],
  ['dueDate', 'dueDateMin', 'dueDateMax'],
  ['dateOfIncident', 'dateOfIncidentMin', 'dateOfIncidentMax'],
  ['dateTime', 'dateTimeMin', 'dateTimeMax'],
  ['endOfLifeHours', 'endOfLifeHoursMin', 'endOfLifeHoursMax'],
  ['lastInspectionDate', 'lastInspectionDateMin', 'lastInspectionDateMax'],
  ['scheduledStartDate', 'scheduledStartDateMin', 'scheduledStartDateMax'],
  ['scheduledEndDate', 'scheduledEndDateMin', 'scheduledEndDateMax'],
  ['startTime', 'startTimeMin', 'startTimeMax'],
  ['endTime', 'endTimeMin', 'endTimeMax'],
  ['dateOfExpense', 'dateOfExpenseMin', 'dateOfExpenseMax'],
  ['lastTime', 'lastTimeMin', 'lastTimeMax'],
  ['nextReviewDate', 'nextReviewDateMin', 'nextReviewDateMax'],
  ['lastReviewDate', 'lastReviewDateMin', 'lastReviewDateMax'],
];
export const multiFilterTypes = [
  'assignee',
  'reviewer',
  'creator',
  'workspaceId',
  'closer',
  'vehicle',
  'supervisor',
  'participants',
  'workspace',
  'status',
  'extension',
  'user',
];

const filterLabelMap = {
  workspaces: keys.common.WORKSPACE,
  workspace: keys.common.WORKSPACE,
  workspaceId: keys.common.WORKSPACE,
  'template.title': keys.common.TITLE,
  templateTitle: keys.common.TITLE,
  'inspection.template.title': keys.common.AUDIT,
  title: keys.common.TITLE,
  type: keys.common.TYPE,
  inspectionType: keys.common.TYPE,
  templateType: keys.common.TYPE,
  subject: 'Subject',
  creator: keys.common.CREATOR,
  creatorId: keys.common.CREATOR,
  assigneeId: keys.common.ASSIGNEE,
  assignee: keys.common.ASSIGNEE,
  assignees: keys.common.ASSIGNEES,
  assigneeInfo: keys.common.ASSIGNEES,
  vehicles: keys.common.ASSETS,
  participants: keys.common.PARTICIPANTS,
  users: keys.common.PARTICIPANTS,
  status: keys.common.STATUS,
  isDraft: keys.common.STATUS,
  documentNumber: keys.assessments.DOCUMENT_NUMBER,
  vehicleId: keys.common.ASSET,
  vehicle: keys.common.ASSET,
  dateModified: keys.common.LAST_UPDATED,
  dateCreated: keys.common.DATE_CREATED,
  dateCompleted: keys.common.DATE_COMPLETED,
  priority: keys.action_items.PRIORITY,
  description: keys.common.DESCRIPTION,
  dueDate: keys.action_items.DUE_DATE,
  labels: keys.common.LABELS,
  unitNumber: keys.assets.UNIT_NUMBER,
  subtype: keys.common.SUBTYPE,
  make: keys.assets.MAKE,
  model: keys.assets.MODEL,
  year: keys.assets.YEAR,
  location: keys.assets.LOCATION,
  lastInspectionDate: keys.assets.LAST_INSPECTION,
  endOfLifeHours: keys.assets.END_OF_LIFE_HOURS,
  endOfLife: keys.assets.END_OF_LIFE_DATE,
  user: keys.common.USER,
  userId: keys.common.USER,
  externalId: 'ID',
  closer: keys.observations.CLOSER,
  reviewer: keys.observations.REVIEWER,
  dateTime: keys.observations.DATE_AND_TIME,
  dateOfIncident: keys.incidents.DATE_OF_INCIDENT,
  closedAt: keys.incidents.CLOSED,
  private: keys.common.PRIVATE,
  auditType: keys.common.TYPE,
  revisionNumber: keys.common.REV,
  narrative: 'Narrative',
  dateAccepted: 'Accepted',
  riskLevel: 'Risk Level',
  target: 'Target',
  interval: keys.tasks.INTERVAL,
  scheduledStartDate: keys.workspace.START_DATE,
  scheduledEndDate: keys.workspace.END_DATE,
  extension: keys.files.FILE_TYPE,
  downloadAllowed: keys.files.DOWNLOAD_ENABLED,
  expires: keys.action.EXPIRES,
  Passed: keys.common.PASSED,
  analysisRequested: keys.templates.SECOND_SIGHT,
  reviewRequired: keys.templates.APPROVAL_REQUIRED,
  documentString: keys.templates.FORM_ID,
  firstName: keys.common.FIRST_NAME,
  lastName: keys.common.LAST_NAME,
  email: keys.settings.EMAIL,
  workRoles: keys.common.ROLE,
  compliance: keys.common.COMPLIANCE,
  supervisor: keys.common.SUPERVISOR,
  role: keys.common.ACCESS,
  externalActivityId: keys.common.RESOURCE_ID,
  payrollId: keys.settings.PAYROLL_ID,
  incidentEmergencyMessage: keys.workspace.INCIDENT_EMERGENCY_MESSAGE,
  locations: keys.common.LOCATION,
  total: keys.expenses.TOTAL,
  dateOfExpense: keys.expenses.HEADERS_DATE,
  activity: keys.timecards.ACTIVITY,
  poNumber: keys.timecards.PO_NUMBER,
  startTime: keys.timecards.START_TIME,
  endTime: keys.timecards.END_TIME,
  billableHours: keys.timecards.HOURS,
  id: 'ID',
  name: keys.common.NAME,
  active: keys.common.ACTIVE,
  requirement: keys.roles.REQUIREMENT,
  negative: keys.observations.ACTION_REQUIRED,
  recognition: keys.observations.RECOGNITION,
  files: keys.common.FILES,
  isObservation: 'Type',
  isRepeat: 'Is Repeat Finding',
  userResponsible: 'User Responsible',
  lastTime: 'Last Time',
  lastStatus: keys.common.STATUS,
  riskScore: 'Initial Score',
  residualRiskScore: 'Mitigated Score',
  category: 'Hazard',
  incidents: 'Incidents',
  extensionStatus: 'Extension Status',
  passed: keys.common.PASSED,
  nextReviewDate: 'Next Review Date',
  lastReviewDate: 'Last Review Date',
  hasRisks: 'Risk Registry',
  reportTo: 'Reports to',
};

const userKeys = [
  'assignee',
  'assignees',
  'assigneeInfo',
  'participants',
  'creator',
  'user',
  'supervisor',
  'users',
  'userResponsible',
  'reviewer',
  'closer',
];

const yesOrNoKeys = [
  'passed',
  'private',
  'analysisRequested',
  'reviewRequired',
  'active',
];

const assessmentTypeKeys = ['templateType', 'inspectionType'];

const getStatusString = (listName, value) => {
  switch (listName) {
    case 'timecards':
      return timecardStatus[value]?.title;
    case 'actionItems':
      return actionItemStatus[value]?.title;
    case 'observations':
      return observationStatus[value]?.title;
    case 'incidents':
      return incidentStatus[value]?.title;
    case 'expenses':
      return expenseStatus[value]?.title;
    case 'assessments':
    case 'audits':
      return assessmentStatus[value].title;
    default:
      return value;
  }
};

export const getFilterChipProps = ({
  filter,
  workspaces,
  users,
  labels,
  vehicles,
  listName,
  riskLevels,
  roles,
}) => {
  let values = typeof filter.value === 'string' ? [filter.value] : filter.value;

  if (assessmentTypeKeys.includes(filter.id)) {
    values = [
      Object.values(defaultAssessmentTypes).find(
        ({ key }) => key === filter.value,
      )?.title ?? filter?.value,
    ];
  }

  if (
    filter.id === 'workspace' ||
    filter?.id === 'workspaceId' ||
    filter?.id === 'workspaces'
  ) {
    const workspacesForCard = [];
    for (const value of filter.value) {
      workspacesForCard.push(workspaces.find(({ id }) => value === id));
    }
    values = workspacesForCard.map((w) => (
      <SimpleWorkspaceCard workspace={w} />
    ));
  }

  if (userKeys.includes(filter.id)) {
    const usersForCard = [];
    for (const value of filter.value) {
      usersForCard.push(users.find(({ id }) => value === id));
    }
    values = usersForCard.map((u) => <SimpleUserCard user={u} />);
  }

  if (filter.id === 'isDraft') {
    values = [
      filter.value === 'false' || filter.value === 'FALSE'
        ? keys.common.COMPLETED
        : keys.common.IN_PROGRESS,
    ];
  }

  if (yesOrNoKeys.includes(filter?.id)) {
    values = [filter.value === 'false' ? keys.common.NO : keys.common.YES];
  }

  if (filter.id === 'labels') {
    const labelsForCard = [];
    for (const value of filter.value) {
      labelsForCard.push(labels.find((l) => l.id === value));
    }
    values = labelsForCard.map((l) => (
      <Label
        key={l.id}
        name={l.name}
        color={l.color}
        rowSized={false}
        value={l.id}
      />
    ));
  }

  if (filter.id === 'vehicle' || filter.id === 'vehicles') {
    const vehiclesForCard = [];

    for (const value of filter.value) {
      vehiclesForCard.push(vehicles.find((v) => v.id === parseInt(value)));
    }
    values = vehiclesForCard.filter(Boolean).map((v) => (
      <Text noMargin value={`${v.id}`}>
        {vehicleString(v)}
      </Text>
    ));
  }

  if (filter.id === 'status') {
    values = [getStatusString(listName, filter.value)];
  }

  if (filter.id === 'riskScore' || filter.id === 'residualRiskScore') {
    const levels = Object.entries(riskLevels)?.map(([key, value]) => ({
      key,
      string: `${value?.min},${value?.max}`,
    }));
    const level = levels?.find(({ string }) => filter?.value === string)?.key;
    return { label: filterLabelMap[filter?.id], values: [level] };
  }

  if (filter.id === 'hasRisks') {
    values = [
      filter?.value === 'true' ? 'Risks Associated' : 'Risks Not Associated',
    ];
  }

  if (filter.id === 'reportTo') {
    const rolesForCard = [];

    for (const value of filter.value) {
      rolesForCard.push(roles.find((r) => r.id === parseInt(value)));
    }
    values = rolesForCard.filter(Boolean).map((r) => (
      <Text noMargin value={`${r.id}`}>
        {r?.title}
      </Text>
    ));
  }

  return {
    label: filterLabelMap[filter?.id],
    values: values?.filter(Boolean),
  };
};

export const defaultSortOptions = (segment) => {
  switch (segment) {
    case 'roles':
    case 'workspaces':
    case 'requirements':
      return 'title';
    case 'settings':
    case 'users':
      return 'firstName';
    default:
      return 'dateModified';
  }
};
export const defaultSearchParams = {
  actionitems: ['type', 'isCorrective'],
  assessments: ['inspectionType'],
};
